import React, { useState, useEffect, useCallback, useRef } from "react";
import Select, { InputActionMeta } from "react-select";
import { searchCity } from "sr/utils/api/city/searchCity";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "sr/redux/store";
import getLabelByValue from "sr/helpers/functions/getLabelByValue";
import { fetchprovince } from "sr/redux/action/master/provinceAction";
import { getRegion } from "sr/utils/api/city/getGeofenceCity";
import { useValidationErrorHandle } from "../functions/validationErrorHandle";

interface Option {
  value: string; // normalized to string
  label: string;
  city: string;
  state: string;
  id: number | string;
  coordinates?: any;
}

interface SmartSearchSelectProps {
  value?: string | number; // allow number as well
  onChange: (value: string | number | any) => void;
  placeholder?: string;
  label?: string;
  error?: any;
  required?: boolean;
  options?: any[];
  disabled?: boolean;
  returnFullData?: boolean;
  className?: string;
}

const SmartSearchSelect: React.FC<SmartSearchSelectProps> = ({
  value,
  onChange,
  placeholder = "Search for a city...",
  label,
  error,
  required = false,
  options: propOptions,
  disabled = false,
  returnFullData = false,
  className = "mt-10",
}) => {
  const [inputValue, setInputValue] = useState("");
  const [debouncedInputValue, setDebouncedInputValue] = useState("");
  const [options, setOptions] = useState<Option[]>([]);
  const [selectedOption, setSelectedOption] = useState<Option | null>(null);
  const [loader, setLoader] = useState(false);
  // Redux
  const provinceData = useSelector((s: RootState) => s.province?.data);
  const dispatch = useDispatch<AppDispatch>();
  const validationErrorHandle = useValidationErrorHandle();

  useEffect(() => {
    // fetch provinces once if not present
    if (!provinceData) {
      dispatch(fetchprovince());
    }
  }, [provinceData, dispatch]);

  const fetchSingleRegion = async (id) => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append("id", id);
      const response = await getRegion(queryParams.toString());
      if (response?.success === true) {
        const city = response?.data;
        const stateFullName = getLabelByValue(city.state, provinceData || []);
        const SingleOptionAsArray = {
          value: String(city.id), // normalize to string for consistent comparisons
          label: `${city.city}, ${stateFullName || city.state || ""}`,
          city: city.city,
          state: city.state || "",
          id: city.id,
          coordinates: city.coordinates,
        };
        setOptions([SingleOptionAsArray]);
      }
    } catch (e) {
      console.error("Error fetching single region:", e);
    }
  };

  // Debounce input
  useEffect(() => {
    const t = setTimeout(() => setDebouncedInputValue(inputValue), 300);
    return () => clearTimeout(t);
  }, [inputValue]);

  // Keep track of request order to avoid race conditions
  const requestCounter = useRef(0);

  // Fetch cities when debouncedInputValue changes or use prop options
  useEffect(() => {
    // console.log('Prop options:', propOptions);
    if (
      propOptions &&
      propOptions.length > 0 &&
      !inputValue &&
      typeof propOptions[0] !== "string"
    ) {
      // Use provided options when no input is typed
      const cityOptions: Option[] = propOptions.map((city: any) => {
        const stateFullName = city.state
          ? getLabelByValue(city.state, provinceData || [])
          : "";

        return {
          value: String(city?.id),
          label: stateFullName ? `${city.city}, ${stateFullName}` : city.city,
          city: city.city,
          state: city.state || "",
          id: city.id,
          coordinates: city.coordinates,
        };
      });

      setOptions(cityOptions);

      // If parent provided a value, ensure selectedOption is set if it matches
      if (value !== undefined && value !== null) {
        const matching = cityOptions.find(
          (opt) => String(opt.value) === String(value)
        );
        if (matching) setSelectedOption(matching);
      }
    } else if (
      propOptions &&
      propOptions.length > 0 &&
      !inputValue &&
      typeof propOptions[0] === "string"
    ) {
      // If propOptions are string, fetch region to get full details
      fetchSingleRegion(propOptions[0]);
    } else {
      // Original fetch logic
      let cancelled = false;
      const currentRequest = ++requestCounter.current;

      const fetchCities = async () => {
        setLoader(true);
        if (debouncedInputValue.length < 2) {
          setOptions([]);
          setLoader(false);
          return;
        }
        try {
          const response = await searchCity(debouncedInputValue);
          // ignore if a later request has started
          if (cancelled || currentRequest !== requestCounter.current) return;

          if (response?.success && response?.data) {
            const cityOptions: Option[] = response.data.map((city: any) => {
              const stateFullName = city.state
                ? getLabelByValue(city.state, provinceData || [])
                : "";
              return {
                value: String(city.id), // normalize to string for consistent comparisons
                label: stateFullName
                  ? `${city.city}, ${stateFullName || city.state || ""}`
                  : city.city,
                city: city.city,
                state: city.state || "",
                id: city.id,
                coordinates: city.coordinates,
              };
            });

            setOptions(cityOptions);
            // If parent provided a value, ensure selectedOption is set if it matches
            if (value !== undefined && value !== null) {
              const matching = cityOptions.find(
                (opt) => String(opt.value) === String(value)
              );
              if (matching) setSelectedOption(matching);
            }
          } else {
            setOptions([]);
          }
        } catch (err) {
          console.error("Error fetching cities:", err);
          if (!cancelled) setOptions([]);
        } finally {
          if (!cancelled) setLoader(false);
        }
      };

      fetchCities();

      return () => {
        cancelled = true;
      };
    }
  }, [debouncedInputValue, provinceData, value, propOptions, inputValue]);

  // Sync when options or incoming `value` prop changes
  useEffect(() => {
    if (value === undefined || value === null) {
      return;
    }
    if (!options.length || typeof options[0] !== "object") return;
    const match = options.find((o) => String(o.value) === String(value));
    if (match) {
      setSelectedOption(match);
    } else {
    }
  }, [value, options]);

  const handleInputChange = useCallback(
    (newValue: string, actionMeta?: InputActionMeta) => {
      // react-select will call this for different actionMeta cases; we only need the newValue
      setInputValue(newValue);
    },
    []
  );

  const handleChange = useCallback(
    (selected: Option | null) => {
      setSelectedOption(selected);
      // send normalized value to parent (use string or number as parent expects)
      if (!selected) {
        onChange(""); // or onChange(undefined) depending on your parent expectation
      } else {
        if (returnFullData) {
          onChange(selected); // Return full selected object
        } else {
          onChange(selected.id ?? selected.value);
        }
      }
    },
    [onChange, returnFullData]
  );

  const customStyles = {
    control: (provided: any, state: any) => ({
      ...provided,
      borderColor: error ? "#ef4444" : state.isFocused ? "#3b82f6" : "#d1d5db",
      "&:hover": {
        borderColor: error ? "#ef4444" : "#3b82f6",
      },
      boxShadow: state.isFocused ? "0 0 0 1px #3b82f6" : "none",
      minHeight: "42px",
    }),
    option: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "#3b82f6"
        : state.isFocused
        ? "#eff6ff"
        : "white",
      color: state.isSelected ? "white" : "#374151",
    }),
  };
  useEffect(() => {
    console.log("isLoading state changed:", loader);
  }, [loader]);
  return (
    <div className={`input-wrapper ${className}`}>
      {label && (
        <label
          className="text-sm font-medium text-gray-700 mb-1 block"
          htmlFor="city-select"
        >
          {label}
          {required && <span className="text-red-500"> *</span>}
        </label>
      )}

      <Select<Option, false>
        inputId="city-select"
        value={selectedOption}
        onChange={(opt) => handleChange(opt as Option | null)}
        onInputChange={handleInputChange}
        options={options}
        placeholder={placeholder}
        isLoading={loader}
        isClearable
        isDisabled={disabled}
        styles={customStyles}
        className="basic-single"
        classNamePrefix="select"
        noOptionsMessage={({ inputValue }) =>
          inputValue.length < 2
            ? "Type at least 2 characters to search"
            : "No cities found"
        }
        loadingMessage={() => "Searching..."}
        onMenuClose={() => {
          // consider whether you want to clear the typed text or not.
          setInputValue("");
        }}
        // aria-label could be set, or inputId + label used for accessibility
      />

      {error && (
        <p className="text-red-500 text-sm mt-1">
          {validationErrorHandle(error)}
        </p>
      )}
    </div>
  );
};

export default SmartSearchSelect;
